import { createContext, useCallback, useContext, useRef } from 'react';
import { Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { StyleSheet } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { PatternBackground } from './Backgrounds';
import CutCard from './CutCard';
import Input from './inputs/Input';
import SolidButton from './SolidButton';

const AuthFocusContext = createContext(null);

export const AuthInput = ({ onFocus, ...props }) => {
    const focusInput = useContext(AuthFocusContext);
    return <Input {...props} onFocus={(event) => {
        focusInput?.(event.target);
        onFocus?.(event);
    }} />;
};
export const AuthButton = (props) => <SolidButton {...props} />;
export const AuthNote = ({ children }) => <Text style={styles.note}>{children}</Text>;

export const AuthHeader = ({ eyebrow, title, description }) => (
    <View style={styles.header}>
        {!!eyebrow && (
            <View style={styles.eyebrowRow}>
                <View style={styles.tick} />
                <Text style={styles.eyebrow}>{eyebrow}</Text>
            </View>
        )}
        <Text style={styles.title}>{title}</Text>
        {!!description && <Text style={styles.description}>{description}</Text>}
    </View>
);

const AuthPage = ({ children, footer, hasHeader = true, maxWidth = 480, showBack = false }) => {
    const insets = useSafeAreaInsets();
    const router = useRouter();
    const scrollRef = useRef(null);
    const focusedInput = useRef(null);
    const scrollToFocusedInput = useCallback(() => {
        if (Platform.OS !== 'web' && focusedInput.current) {
            scrollRef.current?.scrollResponderScrollNativeHandleToKeyboard(focusedInput.current, 24, true);
        }
    }, []);
    const focusInput = useCallback((input) => {
        focusedInput.current = input;
        scrollToFocusedInput();
    }, [scrollToFocusedInput]);

    return (
        <View style={styles.main}>
            <PatternBackground />
            {/* Resize one viewport from its pre-keyboard frame, never the centered content's padding. */}
            <KeyboardAvoidingView style={styles.viewport} enabled={Platform.OS !== 'web'} behavior="height">
                <ScrollView
                    ref={scrollRef}
                    testID="auth-scroll"
                    style={styles.viewport}
                    contentContainerStyle={[styles.content, {
                        paddingTop: insets.top + (hasHeader ? 64 : 24),
                        paddingBottom: insets.bottom + 24,
                    }]}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="on-drag"
                    automaticallyAdjustKeyboardInsets={false}
                    contentInsetAdjustmentBehavior="never"
                    onKeyboardDidShow={() => requestAnimationFrame(scrollToFocusedInput)}
                >
                    <AuthFocusContext.Provider value={focusInput}>
                        {showBack && Platform.OS === 'web' && (
                            <View style={[styles.backContainer, {maxWidth}]}>
                                <Pressable testID="auth-back-button" accessibilityRole="button" onPress={() => router.back()} style={styles.backButton}>
                                    <Text style={styles.backText}>← Back</Text>
                                </Pressable>
                            </View>
                        )}
                        <CutCard testID="auth-panel" accentColor="primary" style={[styles.panel, {maxWidth}]}>
                            {children}
                        </CutCard>
                        {!!footer && <View style={[styles.footer, {maxWidth}]}>{footer}</View>}
                    </AuthFocusContext.Provider>
                </ScrollView>
            </KeyboardAvoidingView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    main: { flex: 1, backgroundColor: colors.background },
    viewport: { flex: 1, width: '100%' },
    content: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 16 },
    panel: { width: '100%', paddingHorizontal: 24, paddingVertical: 28 },
    backContainer: { width: '100%', paddingBottom: 10 },
    backButton: { alignSelf: 'flex-start', _web: { cursor: 'pointer', _hover: { opacity: 0.72 } } },
    backText: { fontFamily: fonts.body, fontSize: 14, color: colors.textSecondary },
    footer: { width: '100%', paddingHorizontal: 16, paddingTop: 24 },
    note: { fontFamily: fonts.body, fontSize: 12, lineHeight: 18, color: colors.onSurfaceVariant, textAlign: 'center' },
    header: { marginBottom: 20, gap: 10 },
    eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    tick: { width: 16, height: 3, backgroundColor: colors.secondary, transform: [{skewX: '-35deg'}] },
    eyebrow: { fontFamily: fonts.bold, fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase', color: colors.primary },
    title: { fontFamily: fonts.bold, fontSize: 28, lineHeight: 32, letterSpacing: -0.8, color: colors.text },
    description: { fontFamily: fonts.body, fontSize: 14, lineHeight: 21, color: colors.onSurfaceVariant },
}));

export default AuthPage;
